import React, { useRef } from "react";
import { Divider, Menu, Stack } from "@mui/material";
import { FiCheckCircle, FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { NOTIFICATION_SECTION } from "../constants";
import { NotificationListSkeleton } from "./NotificationListSkeleton";
import { NotificationSection } from "./NotificationSection";
import { useBottomScroll } from "../_hooks/useBottomScroll";

export function NotificationPanel({
  anchorEl,
  open,
  onClose,
  sections,
  unreadCount,
  isLoading,
  isEmpty,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  onDismiss,
  onMarkAllAsRead,
  isMarkingAll,
}) {
  const { t } = useTranslation();
  const scrollRef = useRef(null);

  const handleScroll = useBottomScroll({
    enabled: Boolean(hasNextPage) && !isFetchingNextPage,
    onReachBottom: onLoadMore,
  });

  return (
    <Menu
      id="notifications-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      MenuListProps={{ disablePadding: true }}
      PaperProps={{ sx: { bgcolor: "transparent", elevation: 0, overflowX: "hidden" } }}
    >
      <Stack direction="column" className="bg-white !w-[400px]">
        <div className="wrap p-2 bg-secondaryColor/10 w-full">
          <div className="flex justify-between items-center py-2">
            <h2 className="text-xl font-semibold">{t("main.clients.notifications")}</h2>

            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={onMarkAllAsRead}
                  disabled={isMarkingAll}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-secondaryColor hover:bg-secondaryColor/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiCheckCircle fontSize={14} />
                  {isMarkingAll
                    ? t("main.notifications.actions.markingAll", { defaultValue: "Marking..." })
                    : t("main.notifications.actions.markAllAsRead", {
                        defaultValue: "Mark all as read",
                      })}
                </button>
              )}

              <button
                type="button"
                onClick={onClose}
                aria-label={t("main.notifications.actions.close", { defaultValue: "Close" })}
                className="hover:bg-gray-100 rounded-md p-1 transition-colors"
              >
                <FiX fontSize={25} />
              </button>
            </div>
          </div>

          <Divider />

          {isLoading ? (
            <NotificationListSkeleton />
          ) : (
            <div
              ref={scrollRef}
              data-testid="custom-scrollbar"
              onScroll={handleScroll}
              className="wrap h-[500px] !w-full custom-scrollbar overflow-y-auto overflow-x-hidden"
            >
              {isEmpty ? (
                <p className="text-gray-500 text-sm text-center py-2">
                  {t("main.clients.noNotifications")}
                </p>
              ) : (
                sections.map((section) => (
                  <NotificationSection
                    key={section.id}
                    section={section}
                    count={section.id === NOTIFICATION_SECTION.NEW ? unreadCount : undefined}
                    onDismiss={onDismiss}
                    onNavigate={onClose}
                  />
                ))
              )}

              {isFetchingNextPage && (
                <div className="py-3 text-center text-sm text-gray-500 w-full">
                  {t("main.notifications.loadingMore", { defaultValue: "Loading more..." })}
                </div>
              )}
            </div>
          )}
        </div>
      </Stack>
    </Menu>
  );
}
